import { useEffect, useState } from "react";
import { BadgePercent, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fileUrl } from "../../lib/api";
import { PageHeader, LoadingRows, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

export default function CreatorOffers() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState("");

  const load = (query = q, c = cat) => api.get("/vid/offers", { params: { q: query, category: c } })
    .then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); setData({ offers: [], categories: [] }); });
  useEffect(() => { load("", ""); }, []);

  const getLink = async (o) => {
    setBusy(o.id);
    try {
      const { data } = await api.post("/vid/creator/links", { offer_id: o.id });
      toast.success("Affiliate link ready!");
      setData((d) => ({ ...d, offers: d.offers.map((x) => x.id === o.id ? { ...x, my_code: data.code } : x) }));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  const copy = async (code) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/vid/r/${code}`;
    try { await navigator.clipboard.writeText(url); setCopied(code); setTimeout(() => setCopied(""), 2000); toast.success("Link copied!"); }
    catch { toast.info(url); }
  };

  const KIND_LABEL = { store_product: "Product", mkt_service: "Service", mkt_product: "Digital Product", mkt_course: "Course" };

  return (
    <div data-testid="creator-offers-page">
      <PageHeader title="Affiliate Products & Services" subtitle="Pick offers, generate your tracking link, earn commission on every conversion" testid="offers-title" />

      <div className="flex flex-wrap gap-3 mb-6">
        <form className="flex-1 min-w-[240px]" onSubmit={(e) => { e.preventDefault(); load(); }}>
          <Input data-testid="offers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search website design, marketing, courses…" className="rounded-full" />
        </form>
        <div className="flex flex-wrap gap-1.5">
          <button data-testid="offers-cat-all" onClick={() => { setCat(""); load(q, ""); }}
            className={`text-xs font-bold px-3.5 py-2 rounded-full ${!cat ? "gradient-bg text-white" : "border border-border text-muted-foreground"}`}>All</button>
          {(data?.categories || []).map((c) => (
            <button key={c} data-testid={`offers-cat-${c.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => { setCat(c); load(q, c); }}
              className={`text-xs font-bold px-3.5 py-2 rounded-full ${cat === c ? "gradient-bg text-white" : "border border-border text-muted-foreground"}`}>{c}</button>
          ))}
        </div>
      </div>

      {!data ? <LoadingRows rows={4} /> : data.offers.length === 0 ? (
        <EmptyState title="No affiliate offers yet" body="Business owners and freelancers will enable affiliate programs on their products & services." icon={BadgePercent} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.offers.map((o) => (
            <div key={o.id} data-testid={`offer-card-${o.id}`} className="bg-card border border-border rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all">
              <div className="h-32 relative">
                {o.image
                  ? <img src={o.image.startsWith("/api/") || o.image.startsWith("/") ? fileUrl(o.image) : o.image} alt="" className="h-full w-full object-cover" />
                  : <div className="h-full w-full" style={{ background: "linear-gradient(135deg,#007A4D,#2563EB,#7C3AED)" }} />}
                <span className="absolute top-2.5 left-2.5 bg-black/70 text-white text-[9px] font-extrabold uppercase px-2 py-1 rounded-md">{KIND_LABEL[o.kind] || o.kind}</span>
                <span className="absolute top-2.5 right-2.5 bg-emerald-500 text-white text-[10px] font-extrabold px-2.5 py-1 rounded-full">
                  {o.commission_type === "pct" ? `${o.commission_value}%` : `₹${o.commission_value}`} commission
                </span>
              </div>
              <div className="p-4 space-y-2">
                <p className="font-display font-bold text-sm leading-snug line-clamp-1">{o.name}</p>
                <p className="text-xs text-muted-foreground">by {o.owner_name} · {o.category || "General"}</p>
                <p className="text-xs text-muted-foreground line-clamp-2 min-h-[2rem]">{o.description}</p>
                <div className="flex items-center justify-between pt-1">
                  <span className="font-display font-extrabold">{fmtINR(o.price)}</span>
                  <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                    You earn {o.commission_type === "pct" ? fmtINR(Math.round(o.price * o.commission_value / 100)) : fmtINR(o.commission_value)}/sale
                  </span>
                </div>
                {o.my_code ? (
                  <div className="flex gap-2">
                    <code className="flex-1 text-[10px] bg-muted rounded-lg px-2.5 py-2 truncate font-mono" data-testid={`offer-code-${o.id}`}>/api/vid/r/{o.my_code}</code>
                    <Button size="sm" variant="outline" data-testid={`offer-copy-${o.id}`} onClick={() => copy(o.my_code)} className="rounded-full font-bold shrink-0">
                      {copied === o.my_code ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                ) : (
                  <Button data-testid={`offer-get-link-${o.id}`} onClick={() => getLink(o)} disabled={busy === o.id}
                    className="w-full rounded-full font-bold gradient-bg text-white border-0">
                    {busy === o.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Get Affiliate Link"}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
